import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DownloadSingleReportConfig,
  manageInvoiceRowsData,
  orderStatusConfig,
} from "../../types/types";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { capitalize, capitalizeFirstLetter } from "../../utils/utils";
import { analytics, logLoadTrace, performance } from "../../../src/firebase";
import { logEvent } from "firebase/analytics";
import { ReactComponent as DownloadAllIcon } from "../../assets/image/svg/downloadAllWhite.svg";
import {
  Alert,
  Button,
  Snackbar,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import {
    getBulkOpertionData,
  getSalesData,
  getUTCDate,
} from "../../services/manageOrderService";
import "./ManageSecondaryInvoice.css";
import "../viewOrder/ViewOrder.css";
import "../manageBasket/ManageBasket.css";
import { Loader } from "../../components/loader/Loader";
import Header from "../../components/dms_common/Header";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid";
import {
  getDownloadSingleReportConfig,
  getOrderSource,
  manageInvoiceColumnsMetaData,
} from "./ManageSecondaryOrderUtils";
import {
  filterData,
  getMultipleFilterBasedData,
  searchWithStatusFilter,
  updateDatesInLocalStorage,
} from "../../components/dms_common/DmsCommonServices";
import {
  isGapMoreThanNDays,
  isStartDateIsGreaterThanEndDate,
} from "../../utils/calenderUtils";
import { downloadAllReportsPrimarySecondary } from "../../services/manageReportService";
import { getConfigData } from "../../services/configurationService";
import ScreenOverlay from "../../components/dms_common/ScreenOverlay";
import { getButtonOnClick, getDisabledButtonSx ,safeStringify,truncateString} from "../../components/dms_common/DmsUtils";
import { GenericModal, getGenericModalProps, initialGenericModalProps } from "../viewOrder/ViewOrderUtils";
import CustomGenericModal from "../../components/dms_common/CustomGenericModal";
import useSessionStorageDates from "../../components/dms_common/UseSessionStorageDates";
import {
  onInvoiceDownLoad,
} from "../../../src/components/loadouts/pre-seller/PresellerLoadoutService";
import { sendSlackAlert, SlackChannel } from "../../services/slackService";

export const GstFlagging: React.FC = () => {
  return (
    <div className="GstFlagging_container">
      <span>GST</span>
    </div>
  );
};

const ManageSecondaryInvoice = () => {
  const ConfigDictionary = JSON.parse(
    localStorage.getItem("ConfigDictionary") ?? "{}"
  );
  const startDateAtLastNDays = ConfigDictionary?.startDateAtLastNDays ?? 0
  const newStartDate = dayjs().subtract(startDateAtLastNDays, 'day')
  const { storedStartDate, storedEndDate, storeSessionStorageDates } =
    useSessionStorageDates("manageInvoices", newStartDate);
  
  const [data, setData] = useState<manageInvoiceRowsData[]>([]);
  const [rows, setRows] = useState<manageInvoiceRowsData[]>([]);
  const [orderTrackingStagesList, setOrderTrackingStagesList] = useState<
    orderStatusConfig[]
  >([]);
  const [startDate, setStartDate] = useState<Dayjs>(storedStartDate);
  const [endDate, setEndDate] = useState<Dayjs>(storedEndDate);
  const [loader, setLoader] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [downloadInvoicePdf, setDownloadInvoicePdf] = useState(false);
  const [multiCategoryFilter, setMultiCategoryFilter] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Dayjs>(dayjs());
  const allOrdersReportIdRef = useRef<string | null>(null);
  const singleOrderReportIdRef = useRef<string | null>(null);
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [disableRowIds, setDisableRowIds] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [filterKeys, setFilterKeys] = useState<string[]>([]); 
  const [selectedFilterType, setSelectedFilterType] = useState("status");
  const [multipleFiltersVisible, setMultipleFiltersVisible] = useState<boolean>(false);
  const [filterSearchValue, setFilterSearchValue] = useState<string>("");
  const [multipleFiltersList, setMultipleFiltersList] = useState<
    Record<string, Record<string, number>>
  >({});
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>(() => {
    const stored = localStorage.getItem('multipleFiltersListManageInvoice');
    return stored ? JSON.parse(stored) : {};
  });
  const fetchLimitedOrderData = useRef<boolean>(false);
  const [downloadSingleReportConfig, setDownloadSingleReportConfig] =
    useState<DownloadSingleReportConfig | null>();
    const [genericModalProps, setGenericModalProps] = useState<GenericModal>(
      initialGenericModalProps
    );
    const [resetSearch, setResetSearch] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

  const feature = "Sale";
  const pageID = "ManageOrder";

  const validDateRange = ConfigDictionary.dateRangeConfig ?? 31;
  function canWeMakeRequest() {
    if (startDate && endDate && isGapMoreThanNDays(startDate, endDate, validDateRange)) {
      setGenericModalProps(
        getGenericModalProps("positive", `You can't select date range more than ${validDateRange} days!`)
      );
      return false;
    }
    if (
      startDate &&
      endDate &&
      isStartDateIsGreaterThanEndDate(startDate, endDate)
    ) {
      setGenericModalProps(
        getGenericModalProps("positive", "To Date should be greater than from date.")
      );
      return false;
    }
    updateDatesInLocalStorage(startDate, endDate, feature, pageID);
    return true;
  }

  const handleApplyLog = async() => {
      logLoadTrace(handleApply,"manage_secondary_invoice_page_load_time");
  }

  const handleApply = async () => {
    if (canWeMakeRequest()) {
      storeSessionStorageDates(startDate, endDate)
      const start = getUTCDate(startDate, "start");
      const end = getUTCDate(endDate, "end");
      await getAllOrderData(start, end);
    }
  };

  const handleClearAllLog = async() => {
      logLoadTrace(handleClearAll,"manage_secondary_invoice_page_load_time");
  }

  const handleClearAll = async () => {
    const date = dayjs();
    storeSessionStorageDates(newStartDate, date);
    const start = getUTCDate(newStartDate, "start");
    const end = getUTCDate(date, "end");
    setStartDate(newStartDate);
    setEndDate(date);
    await getAllOrderData(start, end);
    setSelectedFilters({});
  };

  useEffect(() => {
    localStorage.setItem('multipleFiltersListManageInvoice', JSON.stringify(selectedFilters));
  }, [selectedFilters]);


  const getAllOrderData = async (startDate: string, endDate: string) => {

    setLastRefreshed(dayjs());
    setResetSearch(true); 
    setTimeout(() => setResetSearch(false), 0); 

    try {
      let pageNumber = 0;
      let hasMoreData = true;
      setLoader(true);
      setRows([]);
      setData([]);
      let responseArray: manageInvoiceRowsData[] = [];
      while (hasMoreData){
        const response = await getSalesData("", startDate, endDate, true, pageNumber, undefined, fetchLimitedOrderData.current);

        if (response?.data?.features?.length > 0) {
          hasMoreData = response?.data?.features?.length < 1000 ? false : true
          response?.data?.features?.forEach((item: any, index: any) => {
            const {
              orderNumber: id,
              outletName,
              outletCode,
              creationTime,
              totalCaseQuantity,
              totalPieceQuantity,
              totalOtherUnitQuantity,
              netAmount,
              status,
              orderNumber,
              salesRepName,
              salesRepId,
              invoiceNumber,
              extendedAttributes
            } = item || {};

            const extraParams = status === "INVOICED" ? `?type=invoice` : ``;
            const orderSource = getOrderSource(item);
            const editIcon: boolean = status?.toLowerCase() === "invoiced" && !Boolean(extendedAttributes?.IRNDtls)

            responseArray.push({
              id,
              outletName,
              outletCode,
              creationTime,
              totalCaseQuantity,
              totalPieceQuantity,
              totalOtherUnitQuantity,
              netAmount: netAmount,
              status: status
                .toLowerCase()
                .replace(/(?:^|\s|&)\S/g, (match: any) => match?.toUpperCase()),
              orderNumber,
              source: orderSource,
              extraParams,
              salesRepName,
              salesRepId,
              checkOrderStatus: true,
              invoiceNumber,
              eInvoiced: Boolean(extendedAttributes?.IRNDtls),
              editIcon,
              downloadId: invoiceNumber
            });
          });

          responseArray?.sort((a, b) => {
            const priority = (invoice: manageInvoiceRowsData) => {
              if (invoice.status === "Invoiced" && invoice.eInvoiced) return 2;
              if (invoice.status === "Invoiced") return 3;
              if (invoice.status === "Cancelled") return 1;
              return 0;
            };
          
            return priority(b) - priority(a);
          });

          logEvent(analytics, "secondary_invoice_list_viewed", {
            "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
            "total_invoices_displayed": responseArray?.length
          });
        } else {
          hasMoreData = false;
          // setRows([]);
          // setData([]);
        }
      pageNumber += 1;
    }

    setRows(responseArray);
    setData(responseArray);
    setStatusFilter("ALL"); 
    setSearchedValue("");

    } catch (error) {
      await sendSlackAlert(
        SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
        "Invoice Data Fetch Error - ManageSecondaryInvoice.tsx",
        `Failed to fetch invoice data. Error: ${(error as Error).message}`,
        safeStringify({
          userId: JSON.parse(localStorage.authContext).user.loginId,
          startDate,
          endDate,
          error
        })
      );
      setRows([]);
      setGenericModalProps(
        getGenericModalProps("error", "Something went wrong")
      );
    } finally {
      setLoader(false);
    }
  };


  const handleSelectedFilterChange = (key: string, value: string) => {
    setSelectedFilters((prev) => {
      const current = prev[key] || [];
      return {
        ...prev,
        [key]: current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });
  };

  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };

  const handleStatusFilterChange = (newFilter: string) => {
    setStatusFilter(newFilter);
  };

  const filteredData = useMemo(() => {
    const labelToIdMap = new Map<string, string>();

    orderTrackingStagesList.forEach((stage) => {
      if (stage?.dmsLabel && stage?.id) {
        labelToIdMap.set(stage.dmsLabel.toUpperCase(), stage.id);
      }
    });

    let statuses: string[] = [];
    if (filterKeys.includes("status")) {
      (selectedFilters["status"] || []).forEach((status) => {
        statuses = [...statuses, ...labelToIdMap.get(status.toUpperCase())?.split(',').map((label) => label.trim()) ?? []];
      });
    }

    const updatedSelectedFilters = { ...selectedFilters };
    if (filterKeys.includes("status")) {
      updatedSelectedFilters["status"] = statuses;
    }

    const multipleFilteredData = multiCategoryFilter
      ? getMultipleFilterBasedData(data, updatedSelectedFilters, filterKeys)
      : data;

    const searchFilteredData = filterData(
      multipleFilteredData,
      searchedValue?.toLowerCase(),
      searchWithStatusFilter,
      statusFilter
    );

    const filteredResults = multipleFilteredData.filter((item: any) =>
      new Set(searchFilteredData).has(item)
    );


    return filteredResults;
  }, [
    searchedValue,
    statusFilter,
    data,
    selectedFilters,
    filterKeys,
  ]);

  const handleFilterTypeChange = (type: string) => {
    setSelectedFilterType(type);
    setFilterSearchValue("");
  };

  useEffect(() => {
    setRows(filteredData);
  }, [filteredData]);

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const getInitalTableData = async () => {
    const start = getUTCDate(startDate, "start");
    const end = getUTCDate(endDate, "end");
    await getAllOrderData(start, end);
  };

  const handleDownloadAll = async () => {
    if (isProcessing) return;
    try{
      setIsProcessing(true);
      logEvent(analytics, "manage_invoice_download_all_reports_click", {
        "start_date": startDate,
        "end_date": endDate,
        "login_id": JSON.parse(localStorage.authContext).user.loginId
      });

      const startTime = window.performance.now();

      await downloadAllReportsPrimarySecondary(
        allOrdersReportIdRef.current,
        setSnackbarOpen,
        startDate,
        endDate,
        "type",
        "SecondaryOrder",
        setIsProcessing
      );

      const endTime = window.performance.now();
      logEvent(analytics, "secondary_invoice_report_downloaded", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "report_type": "Invoice",
        "download_format": "xlsx",
        "download_time": (endTime - startTime),
      });
    }catch(error){
      await sendSlackAlert(
        SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
        "Report Download Failure - ManageSecondaryInvoice.tsx",
        `Failed to download all reports. Error: ${(error as Error).message}`,
        safeStringify({
          userId: JSON.parse(localStorage.authContext).user.loginId,
          startDate: startDate.format("YYYY-MM-DD"),
          endDate: endDate.format("YYYY-MM-DD"),
          error
        })
      );
    }
    finally{
    setIsProcessing(false);
  }
  };

  // useEffect(() => {
  //   const onKeyDown = (event: KeyboardEvent) => {
  //     if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "g") {
  //       event.preventDefault(); 
  //       handleDownloadAll();
  //     }
  //   };

  //   window.addEventListener("keydown", onKeyDown);
  //   return () => {
  //     window.removeEventListener("keydown", onKeyDown);
  //   };
  // }, [handleDownloadAll, isProcessing]); 
  const fetchRequiredConfigs = async () => {
    try {
      const orderTrackingStages = await getConfigData(
        "dms_configuration",
        "invoiceStages"
      );
      const salesReportIdConfig = await getConfigData(
        "dms_configuration",
        "salesReportId"
      );
      const allSalesReportIdConfig = await getConfigData(
        "dms_configuration",
        "allSalesReportId"
      );
      const fetchLimitedOrderDataConfig = await getConfigData(
        "dms_configuration",
        "fetchLimitedOrderData"
      );
      const multiCategoryFilterConfig = await getConfigData("dms_configuration","enableMultiCategoryFilterManageInvoice");
      setMultiCategoryFilter(multiCategoryFilterConfig[0]?.configValue ?? false);
      const filterKeysConfig = await getConfigData("dms_configuration","multiFilterKeysManageInvoice");
      setFilterKeys(filterKeysConfig[0]?.configValue ?? ["status", "salesRepName"]);
      const downloadInvoicePdfConfig = await getConfigData("dms_configuration" , "downloadInvoicePdfManageInvoice");
      setDownloadInvoicePdf(downloadInvoicePdfConfig[0]?.configValue ?? false);
      const salesReportId = salesReportIdConfig[0]?.configValue ?? "";
      allOrdersReportIdRef.current = allSalesReportIdConfig[0]?.configValue ?? salesReportId;
      fetchLimitedOrderData.current = fetchLimitedOrderDataConfig?.[0]?.configValue ?? false;
      singleOrderReportIdRef.current = salesReportId;
      setOrderTrackingStagesList(orderTrackingStages[0]?.configValue ?? []);
      setDownloadSingleReportConfig(getDownloadSingleReportConfig(salesReportId));
    } catch (e) {
      await sendSlackAlert(
        SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
        "Configuration Fetch Error - ManageSecondaryInvoice.tsx",
        `Failed to fetch required configurations. Error: ${(e as Error).message}`,
        safeStringify({
          userId: JSON.parse(localStorage.authContext).user.loginId,
          e,
        })
      );
      console.error("Error while fetching configs", e);
      setGenericModalProps(
        getGenericModalProps("error", "Error while fetching configs")
      );
    }
  };

  const initInitialize = async () => {
    await fetchRequiredConfigs();
    await getInitalTableData();
  }

  const init = async () => {
    setLoader(true);
    await Promise.allSettled([initInitialize()])
  };

  useEffect(() => {
    logLoadTrace(init,"manage_secondary_invoice_page_load_time");
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      getInitalTableData();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [startDate, endDate]);

  useEffect(() => {
    const counts: Record<string, Record<string, number>> = {};
    filterKeys.forEach((key) => {
      counts[key] = {};
    });

    if (filterKeys.includes("status")) {
      const labelToIdMap = new Map<string, string>();
      let allDmsLabel: string | undefined;
      orderTrackingStagesList.forEach((stage) => {
        if (stage?.dmsLabel && stage?.id) {
          labelToIdMap.set(stage.dmsLabel.toUpperCase(), stage.id);
          if (stage.id === "ALL") {
            allDmsLabel = stage.dmsLabel.toUpperCase();
          }
        }
      });

      orderTrackingStagesList.forEach((stage: any) => {
        if (stage?.dmsLabel && stage.dmsLabel.toUpperCase() !== allDmsLabel) {
          counts["status"][stage.dmsLabel.toUpperCase()] = 0;
        }
      });

      data.forEach((row: any) => {
        const rowStatus = row?.status?.toUpperCase();
        Object.keys(counts["status"]).forEach((key) => {
          const labels = labelToIdMap.get(key)?.split(',').map((label) => label.trim()) ?? [];
          if (labels.includes(rowStatus)) {
            counts["status"][key] += 1;
          }
        });
      });
    }

    filterKeys.filter((key) => key !== "status").forEach((key) => {
      data.forEach((row: any) => {
        const value = row[key];
        if (typeof value === "string" && value) {
          counts[key][value.toLowerCase()] = (counts[key][value.toLowerCase()] || 0) + 1;
        }
      });
    });

    setMultipleFiltersList(counts);
  }, [orderTrackingStagesList, data, filterKeys]);


  return (
    <div className="manage_order_parent">
      <ScreenOverlay apply={loader} />

      <CustomGenericModal
        type={genericModalProps?.type}
        message={genericModalProps?.message}
        leftButtonText={genericModalProps?.leftButtonText}
        modalOpen={genericModalProps?.open}
        onClose={() => setGenericModalProps(initialGenericModalProps)}
      />
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your report will be downloaded shortly.
        </Alert>
      </Snackbar>
      <Header
        title={"Manage Invoices"}
        searchFunction={handleSearchValueChange}
        onRefresh={initInitialize}
        resetSearch={resetSearch}
        lastRefreshed={lastRefreshed}
      />

      <div className="dms_toolbar">
        <div className="manage_order_support_utility_dates_manageOrder">
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            className="manage_order_dateEnter"
          >
            <DatePicker
              label="From Date"
              inputFormat="DD-MM-YYYY"
              value={startDate}
              maxDate={dayjs()}
              onChange={(value) => {
                if (value) {
                  setStartDate(value);
                }
              }}
              renderInput={(props) => (
                <TextField {...props} inputProps={{
                  ...props.inputProps,
                  readOnly: true,
                }} disabled size="small" />
              )}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="To Date"
              inputFormat="DD-MM-YYYY"
              value={endDate}
              maxDate={dayjs()}
              onChange={(value) => {
                if (value) {
                  setEndDate(value);
                }
              }}
              renderInput={(props) => (
                <TextField {...props}   inputProps={{
                  ...props.inputProps,
                  readOnly: true,
                }}
                 disabled size="small" />
              )}
            />
          </LocalizationProvider>
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#237C9D",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            }}
            onClick={handleApplyLog}
          >
            Apply
          </Button>
          <Button
            variant="outlined"
            sx={{
              textTransform: "none",
              backgroundColor: "#f0fbff",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            }}
            onClick={handleClearAllLog}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            sx={{
              ...getDisabledButtonSx(snackbarOpen),
              textTransform: "none",
              minWidth: "160px",
              backgroundColor: "var(--dashboard-theme-color)",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
                background: "var(--dashboard-theme-color)",
              },
            }}
            onClick={getButtonOnClick(handleDownloadAll, snackbarOpen)}
          >
            <DownloadAllIcon style={{ marginRight: "6px" }} />
            Download All
          </Button>
        </div>
        {multiCategoryFilter ? (
          <FormControl className="manage_secondary_orders_filter">
            <span style={{ fontSize: "14px", color: "#6E6969" }}>Filter:</span>
            <Select
              variant="outlined"
              labelId="dropdown_label"
              open={multipleFiltersVisible}
              value="Filter Invoices"
              onOpen={() => {
                setMultipleFiltersVisible(true);
                setFilterSearchValue("");
              }}
              onClose={() => setMultipleFiltersVisible(false)}
              renderValue={() => (
                <span style={{ color: "#909090", fontSize: "14px" }}>
                  Filter Invoices
                </span>
              )}
              sx={{
                border: Object.values(selectedFilters)?.some(arr => arr?.length > 0)
                  ? "1px solid var(--dashboard-theme-color)"
                  : "1px solid #ccc",
                boxSizing: "border-box",
                height: "37px",
                minWidth: "190px",
                marginLeft: "10px",
                "& .MuiSelect-select": {
                  height: "32px",
                  padding: "6px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#909090",
                  outline: "none",
                  border: "none",
                },
                "& .MuiOutlinedInput-root.Mui-focused": {
                  outline: "none",
                  boxShadow: "none",
                },

                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#DEDEDE",
                  borderWidth: "1px",
                },

                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#DEDEDE",
                },

                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#DEDEDE",
                  borderWidth: "1px",
                },
              }}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "right",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "right",
                },
                PaperProps: {
                  sx: {
                    boxShadow: "none",
                    marginTop: "4px",
                    border: "1px solid #DEDEDE",
                  },
                },
                MenuListProps: {
                  sx: {
                    paddingTop: "8px",
                    paddingBottom: "0",
                  },
                },
              }}
            >
              <MenuItem
                key={`filter-${selectedFilterType}-${JSON.stringify(selectedFilters)}`}
                disableRipple
                style={{
                  padding: 0,
                  backgroundColor: "transparent",
                  outline: "none",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="MSO_filter-dropdown-container"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="MSO_filter-header">
                    <span className="MSO_filter-name">All Filters</span>
                    <span
                      className={`MSO_filter-clear-all ${
                        (Object.values(selectedFilters).some((arr) => arr.length > 0) || 
                          filterSearchValue) &&
                        `MSO_filter-clear-all_active`
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFilters({});
                        setFilterSearchValue("");
                      }}
                    >
                      Clear All
                    </span>
                  </div>
                  <div className="MSO_filter_searchBar_container">
                    <div></div>
                    <input
                      className="MSO_fiter_searchBar"
                      placeholder="Search..."
                      onClick={(e) => e.stopPropagation()}
                      value={filterSearchValue}
                      onChange={(e) => {setFilterSearchValue(e.target.value)
                        e.stopPropagation();
                      }}
                      autoFocus
                      onBlur={(e) => e.target.focus()}
                    />
                  </div>

                  <div className="MSO_filter-options">
                    <div className="MSO_filter-options-left">
                      {filterKeys.map((key) => (
                        <div
                          key={key}
                          className={`${selectedFilterType === key && `MSO_filterSelected`}`}
                          onClick={(e) => {
                            handleFilterTypeChange(key);
                            e.stopPropagation();
                          }}
                        >
                          {key === "salesRepName" ? "SalesRep" : capitalizeFirstLetter(key)} ({(selectedFilters[key] || []).length})
                        </div>
                      ))}
                    </div>
                    <div className="MSO_filter-options-right">
                      {filterKeys.map(
                        (key) =>
                          selectedFilterType === key && (
                            <div className="MSO_filter-option" key={key}>
                              {Object.keys(multipleFiltersList[key] ?? {})
                                .filter((value) =>
                                  filterSearchValue && typeof value === "string"
                                    ? value.toLowerCase().includes(filterSearchValue.toLowerCase())
                                    : true
                                )
                                .map((value) => (
                                  <label
                                    key={value}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      color: "#444444",
                                      fontSize: "13px",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={(selectedFilters[key] || []).includes(value)}
                                      onChange={() => handleSelectedFilterChange(key, value)}
                                      style={{ borderColor: "#CBCBCB" }}
                                    />
                                   {`${
                                      key === "source"
                                        ? value.toUpperCase()
                                        : key === "salesRepName" 
                                         ? `${capitalizeFirstLetter(truncateString(value,20))}`
                                       : capitalizeFirstLetter(value)
                                      } (${multipleFiltersList[key]?.[value] || 0})`}
                                  </label>
                                ))}
                            </div>
                          )
                      )}
                    </div>
                  </div>
                </div>
              </MenuItem>
            </Select>
          </FormControl>
        ) : (
        <FormControl className="manage_order_filter">
          <span
            style={{
              fontSize: "14px",
              color: "#AFAFAF",
            }}
          >
            Filter:
          </span>
          <Select
            labelId="dropdown_label"
            value={statusFilter}
            size="small"
            sx={{ minWidth: "190px", marginLeft: "10px" }}
            onChange={(e) => {
              handleStatusFilterChange(e.target.value);
            }}
            renderValue={(selected) => {
              const item = orderTrackingStagesList.find(
                (d) => d.id === selected
              );
              return item ? (
                <div className="dropdown_selected_item">
                  {capitalize(item?.dmsLabel ?? "")}
                </div>
              ) : null;
            }}
          >
            {orderTrackingStagesList.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                <div className="dropdown_menu_item">
                  <span>{capitalize(item?.dmsLabel ?? "")}</span>
                </div>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        )}
      </div>

      {loader ? (
        <Loader sx={{ position: "absolute", top: "55%" }} />
      ) : (
        <CustomDataGrid
          rows={Array.isArray(rows) ? rows : []}
          columnsMetaData={manageInvoiceColumnsMetaData}
          orderStatusConfig={orderTrackingStagesList}
          redirectUrl="/sale/manageInvoices/viewInvoice/"
          tableWidth={0.723}
          onDownloadGeneric={
            downloadInvoicePdf ?   onInvoiceDownLoad : undefined
          }
          downloadSingleReportConfig={downloadSingleReportConfig}
          enableRowClickRedirect={true}
        />
      )}
    </div>
  );
};

export default ManageSecondaryInvoice;
